// JobClose AI · Account Deletion Safety Bundle A follow-up (2026-08-25)
//
// Minimal Restore-my-account dialog for the sign-in screen. Uses the
// existing green-tested Bundle A endpoint:
//     POST /api/auth/cancel-account-deletion-by-email {email, password}
//
// On success, immediately signs the user in via POST /api/auth/login and
// forwards to /app. Handles the Bundle B restore-race response
// (409 purge_in_progress_or_already_restored) with a definitive
// "cannot be restored" message. Does not expose whether an unrelated
// email exists: 404 user_not_found and 409 not_pending_deletion both
// render as the same generic "no restorable account" copy.

import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, RotateCcw } from "lucide-react";

const COPY = {
  en: {
    trigger: "Restore my account",
    title: "Restore my account",
    body: "If you scheduled your account for deletion within the last 30 days, sign in with the same email and password to restore it.",
    email_label: "Email",
    email_placeholder: "you@example.com",
    password_label: "Password",
    submit_idle: "Restore & sign in",
    submit_loading: "Restoring…",
    cancel: "Never mind",
    ok_restored: "Welcome back — your account is restored.",
    not_restorable:
      "No account is currently scheduled for deletion under those credentials.",
    invalid_credentials:
      "That email and password combination didn't match a restorable account.",
    purge_in_progress:
      "This account has moved past the 30-day recovery window and can no longer be restored. Please contact support if you believe this is an error.",
    generic_error: "Could not restore right now. Please try again in a moment.",
  },
  es: {
    trigger: "Restaurar mi cuenta",
    title: "Restaurar mi cuenta",
    body: "Si programaste la eliminación de tu cuenta en los últimos 30 días, inicia sesión con el mismo correo y contraseña para restaurarla.",
    email_label: "Correo",
    email_placeholder: "tu@ejemplo.com",
    password_label: "Contraseña",
    submit_idle: "Restaurar e iniciar sesión",
    submit_loading: "Restaurando…",
    cancel: "Cancelar",
    ok_restored: "Bienvenido de nuevo — tu cuenta ha sido restaurada.",
    not_restorable:
      "No hay ninguna cuenta programada para eliminación con esas credenciales.",
    invalid_credentials:
      "El correo y la contraseña no coincidieron con una cuenta restaurable.",
    purge_in_progress:
      "Esta cuenta ha pasado el período de recuperación de 30 días y ya no puede restaurarse. Contacta a soporte si crees que es un error.",
    generic_error: "No se pudo restaurar. Intenta de nuevo en un momento.",
  },
};

const RestoreAccountDialog = ({ lang = "en" }) => {
  const c = COPY[lang] || COPY.en;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const reset = () => {
    setOpen(false);
    setEmail("");
    setPassword("");
    setBusy(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) return;
    setBusy(true);
    try {
      // 1. Cancel the pending deletion.
      await api.post("/auth/cancel-account-deletion-by-email", {
        email: email.trim(),
        password,
      });
      // 2. Sign the user in immediately.
      const u = await login(email.trim(), password);
      toast.success(c.ok_restored);
      reset();
      navigate(u.role === "admin" ? "/app/admin" : "/app");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "purge_in_progress_or_already_restored") {
        toast.error(c.purge_in_progress);
      } else if (detail === "invalid_password") {
        toast.error(c.invalid_credentials);
      } else if (detail === "user_not_found" || detail === "not_pending_deletion") {
        // Do not reveal whether the email exists — same generic copy.
        toast.error(c.not_restorable);
      } else {
        toast.error(c.generic_error);
      }
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="login-restore-account-btn"
        className="inline-flex items-center justify-center gap-2 text-[var(--jc-blue)] font-semibold hover:underline"
      >
        <RotateCcw size={16} aria-hidden="true" /> {c.trigger}
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : reset())}>
        <DialogContent data-testid="restore-account-modal">
          <DialogHeader>
            <DialogTitle>{c.title}</DialogTitle>
            <DialogDescription>{c.body}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 py-2">
            <label
              htmlFor="restore-email"
              className="text-xs font-semibold text-[var(--jc-text-soft)] block"
            >
              {c.email_label}
            </label>
            <input
              id="restore-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="restore-account-email-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
              placeholder={c.email_placeholder}
              autoComplete="username"
              required
            />
            <label
              htmlFor="restore-password"
              className="text-xs font-semibold text-[var(--jc-text-soft)] block mt-3"
            >
              {c.password_label}
            </label>
            <input
              id="restore-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="restore-account-password-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
            <DialogFooter className="pt-3">
              <button
                type="button"
                onClick={reset}
                disabled={busy}
                data-testid="restore-account-cancel-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
              >
                {c.cancel}
              </button>
              <button
                type="submit"
                disabled={busy || !email || !password}
                data-testid="restore-account-submit-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold jc-btn-primary disabled:opacity-60 flex items-center gap-1.5"
              >
                {busy ? (
                  <>
                    <Loader2 className="animate-spin" size={13} />{" "}
                    {c.submit_loading}
                  </>
                ) : (
                  c.submit_idle
                )}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RestoreAccountDialog;
